// @flow
import {push, LOCATION_CHANGED} from 'redux-little-router';
import {parseURL, toURL} from '@homeaway/search-url-parser';
import type {SearchParamsType} from '../reducers/searchParams';
import {media} from '../utils/breakpoints';
import modals from '../actions/modals';
import type {ModalType} from '../actions/modals';
import {DATE_MODAL, GUEST_MODAL} from '../actions/modals';
import viewportActions from '../actions/viewport';
import {canonicalizeSearchRequest} from '../utils/searchParamsUtils';
import {MAP_VIEW, LIST_VIEW} from '../constants/views';
import {makeUpdatedSERPUrl} from '../utils/routerUtils';

import type {ThunkAction} from './types';

const ACTIONS = {
    /* redux-little-router dispatches this action as second-order effect from push, replace, et al. It's enumerated here for convenience */
    LOCATION_CHANGED
};

// We should grab this from Map.js, but then each file would import the other
const MapBoundingBoxParams = ['neLat', 'neLong', 'swLat', 'swLong', 'zoom'];
const RegionParams = ['region', 'regionId'];
const mockParams = ['mock', 'mockApi'];
const modalParams = ['modal'];
const pinnedPropertyParams = ['availability.end', 'availability.start'];

const DEFAULT_PARAMS_TO_CLEAR = [
    ...mockParams,
    ...modalParams,
    ...pinnedPropertyParams
];
const CLEAR_LOCATION = [
    ...MapBoundingBoxParams,
    ...RegionParams,
    'searchTermUuid'
];

export const updateURL = (
    newParams: Object,
    paramsToClear: Array<string> = []
): ThunkAction => {
    return (dispatch, getState) => {
        const state = getState();
        const {
            searchParams: {params},
            typeaheadSuggestion
        } = state;

        if (params && params.pinnedPropertyListing && typeaheadSuggestion) {
            params.q = params.q || typeaheadSuggestion.term;
        }

        // params is SearchParamsType
        // canonicalizeSearchRequest takes a SearchRequest, from
        // @homeaway/node-search-url-parser.
        // THEY'RE INCOMPATIBLE. FML.
        // $FlowFixMe
        const canonicalUrl = toURL(canonicalizeSearchRequest(params));

        let newUrl = makeUpdatedSERPUrl(newParams, paramsToClear, state);

        if (canonicalUrl !== newUrl) {
            /* istanbul ignore next */
            if (typeof window !== 'undefined' && window.NEXT_MOCK_API) {
                const delim = newUrl.indexOf('?') > -1 ? '&' : '?';
                newUrl = `${newUrl}${delim}mockApi=${window.NEXT_MOCK_API}`;
                delete window.NEXT_MOCK_API;
            }
            return dispatch(push(newUrl));
        }

        return null;
    };
};

/**
 * Base search method for any search that changes dates, guests, or location
 * @param {string[]} paramsToClear - list of search params to clear
 */
const search = (paramsToClear: string[]) => (newParams: SearchParamsType) => {
    return updateURL({...newParams, page: 1}, paramsToClear);
};
const locationSearch = search(CLEAR_LOCATION);

const THUNK_ACTIONS = {
    /**
     * Updates search params in the URL, will trigger a search request if the parameters have changed
     */
    search: search(DEFAULT_PARAMS_TO_CLEAR),
    /**
     * Performs a new location search clearing all region/map refinements.
     */
    locationSearch,
    filter: (newParams?: SearchParamsType): ThunkAction => (dispatch) => {
        dispatch(viewportActions.scrollYChanged(0));
        dispatch(
            updateURL({
                ...newParams,
                page: 1
            })
        );
    },
    page(page: number) {
        return updateURL({page});
    },
    /**
     * TODO: would be nice to just delete the `view` param entirely to get back
     * to the list, but redux-little-router doesn't easily support that yet
     * c.f. https://github.com/FormidableLabs/redux-little-router/issues/223
     */
    list() {
        return updateURL({view: LIST_VIEW});
    },
    map: (): ThunkAction => (dispatch) => {
        dispatch(viewportActions.scrollYChanged(window.pageYOffset));
        dispatch(
            updateURL({
                view: MAP_VIEW
            })
        );
    },
    mapSearch: (bounds: Object = {}): ThunkAction => (dispatch) => {
        const newParams = {...bounds, page: 1};
        dispatch(viewportActions.scrollYChanged(0));
        dispatch(updateURL(newParams));
    },
    keywords(q: string) {
        return updateURL({q});
    },
    removePinnedProperty() {
        return updateURL({}, ['pinnedPropertyListing']);
    },
    breadcrumb({
        url,
        event
    }: {
        url: string,
        event: SyntheticEvent<*>
    }): ThunkAction {
        return function breadcrumbRouterAction(dispatch, getState) {
            try {
                const {siteName} = getState().system;
                if (!siteName) {
                    return;
                }
                const params: $Shape<SearchParamsType> = parseURL(url, {
                    name: siteName
                });

                // skip G1 landing pages
                if (
                    Object.keys(params).length === 0 ||
                    params.legacyRegionPath
                ) {
                    return;
                }
                // only now should an event be prevented and new search performed
                event.preventDefault();
                dispatch(locationSearch(params));
            } catch (err) {
                // allow click event to proceed as usual, which will perform full refresh
            }
        };
    },
    pushModal(modalType: ModalType): ThunkAction {
        return (dispatch, getState) => {
            const {
                viewport,
                searchParams: {params}
            } = getState();
            const {breakpoint} = viewport;
            const isMobile = !media(breakpoint, {
                minWidth: 'SM_MIN'
            });
            const {modal} = params;
            const modalArray = [].concat(modal || []);

            // Keep track of scroll position so that the Application component
            // can restore it after the modal is finished popping
            dispatch(viewportActions.scrollYChanged(window.pageYOffset));

            if (!isMobile && modalType === DATE_MODAL) {
                dispatch(modals.toggle(DATE_MODAL, true));
            }

            if (!isMobile && modalType === GUEST_MODAL) {
                dispatch(modals.toggle(GUEST_MODAL, true));
            }

            // For mobile, middleware dispatches modal open
            if (isMobile && !modalArray.includes(modalType)) {
                dispatch(
                    push(
                        toURL(
                            // $FlowFixMe (see above)
                            canonicalizeSearchRequest({
                                ...params,
                                modal: [...modalArray, modalType]
                            })
                        )
                    )
                );
            }
        };
    },
    popModal(modalType: ModalType): ThunkAction {
        return (dispatch, getState) => {
            const {
                viewport,
                searchParams: {params}
            } = getState();

            const {breakpoint} = viewport;
            const isMobile = !media(breakpoint, {minWidth: 'SM_MIN'});

            if (!isMobile && modalType === DATE_MODAL) {
                dispatch(modals.toggle(DATE_MODAL, false));
            }

            if (!isMobile && modalType === GUEST_MODAL) {
                dispatch(modals.toggle(GUEST_MODAL, false));
            }

            // For mobile, middleware dispatches modal close
            const {modal} = params;
            const modalArray = [].concat(modal || []).filter((value) => {
                return value !== modalType;
            });
            if (isMobile && modal) {
                dispatch(
                    push(
                        toURL(
                            // $FlowFixMe FML FML FML FML
                            canonicalizeSearchRequest({
                                ...params,
                                modal: modalArray
                            })
                        )
                    )
                );
            }
        };
    }
};

export default {
    ...THUNK_ACTIONS,
    ...ACTIONS
};
